import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  approveFloorInwardRequest,
  clearSelectedRequestItems,
  getFloorRequestById,
} from "../../../store/slices/Factory/newFloorSlice";
import { useEffect, useState } from "react";
import { IFloorRequest } from "../../../models/floor.model";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";
import { usePreventWheelChange } from "../../../utils/usePreventWheelChange";
import FloorRequestStatusBadge from "./FloorStatusBadge";

interface FloorRequestDetailsModalProps {
  onClose: (success: boolean) => void;
  request?: IFloorRequest;
}

export default function FloorRequestDetailsModal({
  onClose,
  request,
}: FloorRequestDetailsModalProps) {
  const dispatch: RootDispatch = useDispatch();
  const handleWheelPrevent = usePreventWheelChange();
  const items = useSelector(
    (state: RootState) => state.newFloor.selectedRequestitems,
  );
  const loading = useSelector(
    (state: RootState) => state.newFloor.loading.getFloorRequestByIdLoading,
  );
  const { requestNo, requestedBy, createdAt } = request!;

  // State for editable quantities
  const [editedQuantities, setEditedQuantities] = useState<{
    [key: string]: { qty: number; unit: string };
  }>({});

  // Array state storing IDs of selected rows
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  useEffect(() => {
    dispatch(getFloorRequestById({ id: request!._id }));
  }, [dispatch, request]);

  // Initialize editable quantities when items load
  useEffect(() => {
    if (items) {
      const initialQuantities: {
        [key: string]: { qty: number; unit: string };
      } = {};
      items.forEach((item) => {
        let qty = item.requestedQuantity - item.fulfilledQuantity;
        if (qty === 0) qty = item.fulfilledQuantity;
        initialQuantities[item._id] = {
          qty,
          unit: item.unit,
        };
      });
      setEditedQuantities(initialQuantities);
      setSelectedItemIds([]); // Reset selection on load
    }
  }, [items]);

  const handleQuantityChange = (
    id: string,
    val: { qty: number; unit: string },
  ) => {
    setEditedQuantities((prev) => ({ ...prev, [id]: val }));
  };

  // Checkbox Selection Logic
  const handleSelectRow = (id: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
    if (!items) return;
    const filteredItems = items.filter(
      (item) => item.status !== "approved" && item.status !== "rejected",
    );
    if (selectedItemIds.length === filteredItems.length) {
      setAllselected(false);
    } else {
      setAllselected(true);
    }
  };
  const [allSelected, setAllselected] = useState(false);

  const handleSelectAll = () => {
    if (!items) return;
    const filteredItems = items.filter(
      (item) => item.status !== "approved" && item.status !== "rejected",
    );
    if (selectedItemIds.length === filteredItems.length) {
      setSelectedItemIds([]); // Deselect all
      setAllselected(false);
    } else {
      setSelectedItemIds(filteredItems.map((item) => item._id)); // Select all
      setAllselected(true);
    }
  };

  // Global action handlers using the selected array and modified quantities
  const handleGlobalApprove = async () => {
    const data: {
      requestItemId: string;
      approvedQuantity: number;
      unit: string;
    }[] = [];

    selectedItemIds.forEach((id) => {
      const approved = editedQuantities[id];
      if (approved.qty > 0) {
        data.push({
          requestItemId: id,
          approvedQuantity: approved.qty,
          unit: approved.unit,
        });
      }
    });

    if (data.length > 0) {
      try {
        const res = await dispatch(
          approveFloorInwardRequest({ id: request!._id!, data }),
        ).unwrap();

        onClose(res.success || false);
      } catch (err: any) {
        console.log(err);
      }
    }
  };

  const handleGlobalReject = () => {
    console.log("Rejecting Selected IDs:", selectedItemIds);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-2 sm:p-4 backdrop-blur-xs">
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-6 sm:py-4 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">
              Floor Request Details
            </h2>
            <p className="text-xs text-gray-500 hidden sm:block">
              Select items to batch process approvals or rejections
            </p>
          </div>
          <button
            onClick={() => {
              dispatch(clearSelectedRequestItems());
              onClose(false);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors text-xl font-medium"
          >
            &times;
          </button>
        </div>

        {/* Summary Info Strip */}
        <div className="grid grid-cols-2 gap-3 border-b border-gray-100 p-4 sm:p-5 sm:grid-cols-3 bg-white">
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Request No
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {requestNo}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Requested By
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {requestedBy?.username}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 col-span-2 sm:col-span-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Requested Date
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {formatToDDMMYYYY(createdAt)}
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50/30">
          {loading ? (
            <div className="flex justify-center items-center py-12 text-gray-500 text-sm">
              Loading request details...
            </div>
          ) : !items || items.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400 font-medium bg-white border-b">
              No items found.
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <table className="hidden md:table w-full border-collapse text-left text-sm bg-white">
                <thead className="sticky top-0 bg-gray-100 text-gray-600 font-medium border-b border-gray-200 shadow-xs z-10">
                  <tr>
                    <th className="px-4 py-3 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3 font-semibold">Material</th>
                    <th className="px-3 py-3 text-right font-semibold">
                      Requested
                    </th>
                    <th className="px-3 py-3 text-center font-semibold w-36">
                      Approve Qty
                    </th>
                    <th className="px-3 py-3 text-right font-semibold">
                      Fulfilled
                    </th>
                    <th className="px-3 py-3 text-center font-semibold">
                      Unit
                    </th>
                    <th className="px-3 py-3 text-center font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <tr
                      key={item._id}
                      className={`transition-colors ${selectedItemIds.includes(item._id) ? "bg-blue-50/40 hover:bg-blue-50/60" : "hover:bg-gray-50/70"}`}
                    >
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          disabled={
                            item.status === "approved" ||
                            item.status === "fulfilled"
                          }
                          checked={selectedItemIds.includes(item._id)}
                          onChange={() => handleSelectRow(item._id)}
                          className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {item.rawMaterialId?.name}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600">
                        {item.requestedQuantity}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          onWheel={handleWheelPrevent}
                          disabled={item.status === "approved"}
                          min="0"
                          value={
                            editedQuantities[item._id] !== undefined
                              ? editedQuantities[item._id].qty
                              : item.requestedQuantity
                          }
                          onChange={(e) =>
                            handleQuantityChange(item._id, {
                              qty: Number(e.target.value),
                              unit: item.unit,
                            })
                          }
                          className={`w-full rounded-md border border-gray-300 px-2 py-1 text-right text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-hidden transition-all ${item.status === "approved" ? "bg-slate-200 text-slate-500" : "bg-white"}`}
                        />
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600">
                        {item.fulfilledQuantity}
                      </td>
                      <td className="px-3 py-3 text-center text-gray-500 font-medium">
                        {item.unit}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <FloorRequestStatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card-Based List View */}
              <div className="block md:hidden p-3 space-y-3">
                <div className="flex items-center justify-between px-1.5 py-1 text-xs bg-gray-100 rounded-lg text-gray-600">
                  <span className="font-medium">Select All Items</span>
                  <input
                    type="checkbox"
                    checked={
                      items.length > 0 &&
                      selectedItemIds.length === items.length
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </div>

                {items.map((item) => {
                  const isChecked = selectedItemIds.includes(item._id);
                  return (
                    <div
                      key={item._id}
                      className={`rounded-xl border p-3.5 shadow-xs space-y-3 transition-all ${
                        isChecked
                          ? "border-blue-300 bg-blue-50/20"
                          : "border-gray-200/80 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2.5">
                          <input
                            type="checkbox"
                            disabled={item.status === "approved"}
                            checked={isChecked}
                            onChange={() => handleSelectRow(item._id)}
                            className="h-4 w-4 mt-0.5 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm leading-tight">
                              {item.rawMaterialId?.name}
                            </h4>
                            <span className="text-[11px] font-medium text-gray-500 mt-0.5 inline-block bg-gray-100 rounded px-1.5 py-0.2">
                              Unit: {item.unit}
                            </span>
                          </div>
                        </div>
                        <FloorRequestStatusBadge status={item.status} />
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-1 text-center border-t border-b border-gray-100 py-2 bg-gray-50/50 rounded-lg">
                        <div>
                          <span className="block text-[10px] font-medium text-gray-400 uppercase">
                            Req
                          </span>
                          <span className="text-xs font-semibold text-gray-700">
                            {item.requestedQuantity}
                          </span>
                        </div>
                        <div>
                          <span className="block text-[10px] font-medium text-gray-400 uppercase">
                            Fulfilled
                          </span>
                          <span className="text-xs font-semibold text-gray-700">
                            {item.fulfilledQuantity}
                          </span>
                        </div>
                        <div className="px-1">
                          <span className="block text-[10px] font-medium text-gray-400 uppercase mb-0.5">
                            Approve
                          </span>
                          <input
                            type="number"
                            onWheel={handleWheelPrevent}
                            disabled={item.status === "approved"}
                            min="0"
                            value={
                              editedQuantities[item._id] !== undefined
                                ? editedQuantities[item._id].qty
                                : item.requestedQuantity
                            }
                            onChange={(e) =>
                              handleQuantityChange(item._id, {
                                qty: Number(e.target.value),
                                unit: item.unit,
                              })
                            }
                            className={`w-full rounded border border-gray-300 py-0.5 px-1 text-center text-xs font-bold focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-hidden ${item.status === "approved" ? "bg-slate-200 text-slate-500" : "bg-white"}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Global Action Footer */}
        <div className="border-t border-gray-100 bg-gray-50 p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
          <div className="text-center sm:text-left">
            <p className="text-xs text-gray-500 font-medium">
              Selected:{" "}
              <span className="font-bold text-blue-600">
                {selectedItemIds.length}
              </span>{" "}
              of{" "}
              <span className="font-bold text-gray-800">
                {items?.length || 0}
              </span>
            </p>
          </div>
          <div className="grid grid-cols-2 sm:flex items-center gap-2">
            <button
              onClick={handleGlobalReject}
              disabled={loading || selectedItemIds.length === 0}
              className="w-full sm:w-auto px-5 py-2 rounded-lg bg-rose-600 text-white text-xs sm:text-sm font-semibold tracking-wide hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
            >
              Reject Selected
            </button>
            <button
              onClick={handleGlobalApprove}
              disabled={loading || selectedItemIds.length === 0}
              className="w-full sm:w-auto px-5 py-2 rounded-lg bg-emerald-600 text-white text-xs sm:text-sm font-semibold tracking-wide hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
            >
              Approve Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
